import { supo, hipNew } from '../api';
import ActionTypes from '../constants/ActionTypes';
import { getToken } from '../selectors/user';
import { getUserLoginState } from '../selectors/user';

const fileMap = new Map();

export const fetchContacts = (limit = 0, offset = 0) => {
  return async dispatch => {
    dispatch({
      type: ActionTypes.FETCH_CONTACTS_REQUEST
    });
    try {
      const { data } = await hipNew.get(`/message_centre/get_contacts`, {
        params: { v: 2, offset, limit }
      });
      const contacts = data.data[0].extra_data.message_groups;
      return dispatch({
        type: ActionTypes.FETCH_CONTACTS_SUCCESS,
        payload: contacts
      });
    } catch (error) {
      return dispatch({
        type: ActionTypes.FETCH_CONTACTS_ERROR,
        payload: error
      });
    }
  };
};

export const fetchThread = (id, refresh = false) => {
  return async (dispatch, getState) => {
    const token = getToken(getState());
    if (!refresh) {
      dispatch({
        type: ActionTypes.FETCH_THREAD_REQUEST,
        payload: { id }
      });
    }
    try {
      const { data: messages } = await supo.get(`/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      dispatch(markThreadAsRead(id));
      return dispatch({
        type: ActionTypes.FETCH_THREAD_SUCCESS,
        payload: { id, messages }
      });
    } catch (error) {
      return dispatch({
        type: ActionTypes.FETCH_THREAD_ERROR,
        payload: { id },
        error
      });
    }
  };
};

/**
 * Hits the `mark-read `API and updates the `numberUnreadMessages` count on the given connection
 *
 * @param {number} id - The id of the thread/connection/assignment
 */
const markThreadAsRead = id => {
  return async dispatch => {
    await hipNew.post(`/api/success/mark-read`, {
      assignment_id: id
    });
    return dispatch({
      type: ActionTypes.UPDATE_CONNECTION,
      payload: { id, numberUnreadMessages: 0 }
    });
  };
};

let counter = 1;
const tempKey = () => +new Date() + ++counter;

const transformMessage = ({ data }) => {
  const { message, message_content } = data.data;
  return {
    id: message.message_id,
    date: message.created_by_date,
    body: message_content.body,
    userId: message.user_id,
    state: null
  };
};

const transformAttachment = ({ data }, filename) => {
  const { message, message_attachments } = data.data;
  const attachment = message_attachments[0];
  const [originalName, extension] = filename.split('.');
  return {
    id: message.message_id,
    date: message.created_by_date,
    body: '',
    userId: message.user_id,
    type: 5,
    typeData: {
      name: `${attachment.url}?jwt=${attachment.jwt_token}`,
      thumbnail: `${attachment.url}?jwt=${attachment.jwt_token}`,
      size: attachment.size,
      meta: attachment.meta,
      originalName,
      extension,
      progress: 100
    }
  };
};

export const sendMessage = (connection, body, existingKey = null, fetchOnCompletion = true) => {
  const key = existingKey || tempKey();
  return async function(dispatch, getState) {
    const { id: userId } = getUserLoginState(getState());
    const { messageGroupId } = connection;
    dispatch({
      type: ActionTypes.UPDATE_THREAD,
      payload: {
        id: messageGroupId,
        messages: [
          {
            id: key,
            body,
            date: new Date(),
            status: 0,
            userId,
            state: 'pending'
          }
        ]
      }
    });
    try {
      const form = new FormData();
      form.append('message_group_id', Number(messageGroupId) as any);
      form.append('message_content', body);
      const result = await hipNew.post('/message_centre/send', form, {
        params: { ajax: 1 }
      });
      dispatch({
        type: ActionTypes.UPDATE_MESSAGE,
        payload: {
          id: key,
          thread: messageGroupId,
          message: transformMessage(result)
        }
      });
    } catch (e) {
      dispatch({
        type: ActionTypes.UPDATE_THREAD,
        payload: {
          id: messageGroupId,
          messages: [
            {
              id: key,
              body,
              date: new Date(),
              status: 0,
              userId,
              error: true,
              state: 'error'
            }
          ]
        }
      });
    }

    if (fetchOnCompletion) {
      dispatch(fetchThread(connection.messageGroupId));
    }

    return key;
  };
};

export const uploadImage = (connection, file, existingKey = null) => {
  const key = existingKey || tempKey();
  return async function(dispatch, getState) {
    const { id: userId } = getUserLoginState(getState());
    const { messageGroupId } = connection;
    const fileBlob = await fileToBase64(file);
    const [originalName, extension] = file.name.split('.');
    const typeData = {
      name: fileBlob,
      thumbnail: fileBlob,
      originalName,
      extension
    };
    dispatch({
      type: ActionTypes.UPDATE_THREAD,
      payload: {
        messageGroupId,
        messages: [
          {
            id: key,
            date: new Date(),
            status: 0,
            userId,
            type: 5,
            typeData
          }
        ]
      }
    });
    const form = new FormData();
    form.set('files', file);
    form.set('message_content', '');
    form.set('message_group_id', Number(messageGroupId) as any);
    form.set('extra_user_id', Number(userId) as any);
    try {
      const response = await hipNew.post('/message_centre/send', form, {
        onUploadProgress: progressEvent => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          dispatch({
            type: ActionTypes.UPDATE_THREAD,
            payload: {
              messageGroupId,
              messages: [
                {
                  id: key,
                  typeData: {
                    ...typeData,
                    progress
                  }
                }
              ]
            }
          });
        }
      });
      dispatch({
        type: ActionTypes.UPDATE_MESSAGE,
        payload: {
          id: key,
          thread: messageGroupId,
          message: transformAttachment(response, file.name)
        }
      });
    } catch (e) {
      fileMap.set(key, file);
      dispatch({
        type: ActionTypes.UPDATE_THREAD,
        payload: {
          messageGroupId,
          messages: [
            {
              id: key,
              typeData: {
                ...typeData,
                progress: -1
              }
            }
          ]
        }
      });
    }
    dispatch(fetchThread(connection.messageGroupId));
  };
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

export function resendAttachment(connection, id) {
  return dispatch => dispatch(uploadImage(connection, fileMap.get(id), id));
}

export function deleteMessage(connection, message) {
  return {
    type: ActionTypes.DELETE_MESSAGE,
    payload: {
      thread: connection.messageGroupId,
      message: message
    }
  };
}

export function updatePaymentStatus(connection, message, status) {
  return dispatch => {
    dispatch({
      type: ActionTypes.UPDATE_THREAD,
      payload: {
        id: connection.messageGroupId,
        messages: [
          {
            id: message.id,
            typeData: {
              ...message.typeData,
              status
            }
          }
        ]
      }
    });
    dispatch(fetchThread(connection.messageGroupId));
  };
}
